import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Trenger restauranten din online bordreservasjon?",
  },
  {
    id: "definition",
    label: "Hva er online bordreservasjon?",
  },
  {
    id: "phone",
    label: "Hvorfor telefonreservasjoner ikke alltid er nok",
  },
  {
    id: "always-on",
    label: "Online reservasjon fungerer utenfor åpningstid",
  },
  {
    id: "menu",
    label: "Hvordan online reservasjon henger sammen med en digital meny",
  },
  {
    id: "planning",
    label: "Hvordan online reservasjon hjelper med planlegging",
  },
  {
    id: "no-show",
    label: "No-shows: hva online reservasjon kan og ikke kan løse",
  },
  {
    id: "for-whom",
    label: "Hvilke restauranter har mest nytte av online reservasjon?",
  },
  {
    id: "mistakes",
    label: "Vanlige feil med online reservasjon",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place kan hjelpe med online reservasjon",
  },
  {
    id: "faq",
    label: "FAQ om online bordreservasjon",
  },
];

const checklist = [
  "Gjester ringer ofte i travle serviceperioder.",
  "Teamet ditt kan ikke alltid svare på telefonen med en gang.",
  "Reservasjoner kommer via Google, Instagram, nettsiden eller meldinger.",
  "Du vil ta imot reservasjonsforespørsler utenfor åpningstid.",
  "Du vil planlegge bord og gjester tydeligere på forhånd.",
  "Du vil ha mindre kaos med notater, telefoner og meldinger.",
  "Du vil ha dato, tid, antall personer og kontaktinformasjon samlet ett sted.",
  "Reservasjon bør henge sammen med menyen eller restaurantsiden.",
  "Restauranten bør føles enklere for yngre gjester og turister.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Online reservasjon er kanskje ikke akutt ennå",
      text:
        "Telefon eller en enkel kontaktmulighet kan fortsatt være nok. Men en liten online reservasjonsflyt kan hjelpe deg med å samle forespørsler på en mer strukturert måte.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "En test gir mening",
      text:
        "Online bordreservasjon kan hjelpe restauranten med å redusere telefonstress, samle reservasjonsdetaljer tydeligere og gi gjestene en mer praktisk vei til bordbestilling.",
      tone: "medium",
    };
  }

  return {
    title: "Online reservasjon kan bli en viktig del av systemet ditt",
    text:
      "Flere punkter viser at digitale reservasjoner kan hjelpe restauranten din: bedre planlegging, færre tapte forespørsler, kortere vei fra meny til booking og enklere tilgang for gjester.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function OnlineTableReservationForRestaurantsNo() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Online bordreservasjon for restauranter | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/online-tischreservierung",
          en: "/en/online-table-reservation-for-restaurants",
          no: "/no/online-bordreservasjon-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Online bordreservasjon for restauranter: mindre telefonstress,
              mer forutsigbare gjester
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Online reservasjon erstatter ikke gjestfrihet. Den fjerner
                unødvendig friksjon før gjesten i det hele tatt kommer til
                restauranten.
              </p>

              <p className="seoHero__text">
                Når en gjest har sett menyen din, funnet restauranten på Google
                eller åpnet siden din via en QR-kode, er neste spørsmål enkelt:
                kan jeg reservere et bord akkurat nå?
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Trenger restauranten din online bordreservasjon?</h2>

            <p>
              Trykk på utsagnene som passer for restauranten din. Resultatet er
              bevisst ærlig: ikke alle restauranter trenger online reservasjon
              med en gang, men mange kan ha nytte av en enkel bookingflyt mye
              tidligere enn de tror.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Hva er online bordreservasjon?</h2>

          <p>
            Online bordreservasjon betyr at gjester kan velge dato, tidspunkt,
            antall personer og legge inn kontaktinformasjon via restaurantens
            nettside eller online restaurantside.
          </p>

          <p>
            For gjester er dette praktisk fordi de slipper å ringe, vente eller
            sende meldinger via sosiale medier. For restauranter gir det en
            tydeligere prosess: reservasjoner blir ikke spredt mellom
            telefonsamtaler, lapper, Instagram-meldinger og hukommelse.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Dato.</li>
            <li className="text__seo-li">Tidspunkt.</li>
            <li className="text__seo-li">Antall personer.</li>
            <li className="text__seo-li">Navn.</li>
            <li className="text__seo-li">Telefon eller e-post.</li>
            <li className="text__seo-li">Kommentar.</li>
            <li className="text__seo-li">Reservasjonsstatus.</li>
          </ul>

          <h3>Reservasjoner bør være strukturerte, ikke spredte</h3>

          <p>
            Den største fordelen er ikke bare at gjesten kan sende en
            forespørsel online. Restauranten mottar også viktig informasjon i et
            tydeligere format.
          </p>
        </section>

        <section id="phone" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor telefonreservasjoner ikke alltid er nok</h2>

            <p>
              Telefonen er fortsatt viktig. Men den har én svakhet: den fungerer
              bare godt når noen har tid til å svare med en gang.
            </p>

            <h3>Et tapt anrop kan bli et tapt bord</h3>

            <p>
              I travle perioder kan teamet være midt i service, gjester kan
              vente, kjøkkenet kan være opptatt, og telefonen kan ringe på det
              dårligste tidspunktet.
            </p>

            <p>
              Online reservasjon fjerner ikke telefonen. Den legger til en
              ekstra kanal som kan fungere parallelt.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Bare telefon</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Noen må svare med en gang.
                  </li>
                  <li className="text__seo-li">
                    Anrop avbryter servicen.
                  </li>
                  <li className="text__seo-li">
                    Detaljer kan skrives feil.
                  </li>
                  <li className="text__seo-li">
                    Forespørsler utenfor åpningstid kan bli oversett.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Telefon + online forespørsel</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Gjester kan fortsatt ringe hvis de foretrekker det.
                  </li>
                  <li className="text__seo-li">
                    Online forespørsler samler viktige detaljer.
                  </li>
                  <li className="text__seo-li">
                    Teamet kan se gjennom forespørsler senere.
                  </li>
                  <li className="text__seo-li">
                    Færre reservasjoner går tapt i travle øyeblikk.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="always-on" className="acquaintance">
          <h2>Online reservasjon fungerer utenfor åpningstid</h2>

          <p>
            Gjester planlegger ikke alltid restaurantbesøk i åpningstiden. De
            kan bestemme seg sent på kvelden, i helgen, mens de reiser, eller
            rett etter at de har sett menyen.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>23:14:</strong> Gjesten åpner menyen på en smarttelefon.
            </li>

            <li className="text__seo-li">
              <strong>23:16:</strong> Gjesten velger dato, tid og antall personer.
            </li>

            <li className="text__seo-li">
              <strong>23:17:</strong> Reservasjonsforespørselen sendes.
            </li>

            <li className="text__seo-li">
              <strong>Morgen:</strong> Restauranten ser den nye forespørselen.
            </li>
          </ol>

          <p>
            Dette er den praktiske verdien: online reservasjon fortsetter å
            fungere når teamet ikke er tilgjengelig på telefon.
          </p>

          <VisualIdea>
            En enkel kveldsflyt: gjesten åpner menyen om kvelden, trykker på
            reserver, sender en forespørsel, og restauranten ser den om
            morgenen.
          </VisualIdea>
        </section>

        <section id="menu" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvordan online reservasjon henger sammen med en digital meny</h2>

            <p>
              Digital meny og online reservasjon bør ikke behandles som helt
              separate ideer. Ofte sjekker gjesten først menyen, ser på priser,
              bilder og tilbud — og bestemmer seg deretter for om han eller hun
              vil besøke restauranten.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">Se menyen.</li>
              <li className="text__seo-li">Sjekke retter og priser.</li>
              <li className="text__seo-li">Interesse oppstår.</li>
              <li className="text__seo-li">Reservere bord.</li>
            </ol>

            <p>
              Når en tydelig «Reserver bord»-knapp vises etter menyen, blir veien
              kortere. Menyen svarer på spørsmålet: hva kan jeg få her?
              Reservasjonen svarer på spørsmålet: hvordan kan jeg komme dit?
            </p>

            <div className="seoCompareSmall">
              <div>
                <strong>Relatert side</strong>
                <p>
                  <Link to="/no/digital-meny-for-restauranter">
                    Digital meny for restauranter
                  </Link>
                </p>
              </div>

              <div>
                <strong>Relatert side</strong>
                <p>
                  <Link to="/no/qr-kode-meny-uten-app">
                    QR-kode-meny uten app
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="planning" className="acquaintance">
          <h2>Hvordan online reservasjon hjelper med planlegging</h2>

          <p>
            Når reservasjoner kommer gjennom mange ulike kanaler, oppstår kaos
            lett. Online reservasjon hjelper med å samle viktig informasjon i et
            konsekvent format.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Når kommer gjesten?</li>
            <li className="text__seo-li">Hvor mange personer?</li>
            <li className="text__seo-li">Finnes det en kommentar?</li>
            <li className="text__seo-li">Er reservasjonen bekreftet?</li>
            <li className="text__seo-li">Finnes det spesielle notater?</li>
            <li className="text__seo-li">Trenger noen en tilbakeringing?</li>
          </ul>

          <p>
            Jo bedre restauranten ser reservasjonene sine, jo bedre kan den
            planlegge kvelden: bord, personale, belastning på kjøkkenet,
            travle tider og større grupper.
          </p>
        </section>

        <section id="no-show" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>No-shows: hva online reservasjon kan og ikke kan løse</h2>

            <p>
              Online reservasjon er ingen garanti mot no-shows. Gjester kan
              fortsatt la være å møte opp. Men det kan gjøre prosessen tydeligere
              og mer strukturert.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">
                Kontaktinformasjon samles tydelig.
              </li>
              <li className="text__seo-li">
                Bekreftelser blir lettere å forstå.
              </li>
              <li className="text__seo-li">
                Kanselleringer kan bli enklere.
              </li>
              <li className="text__seo-li">
                Kommentarer og spesielle ønsker blir dokumentert.
              </li>
              <li className="text__seo-li">
                Teamet ser reservasjonene ett sted.
              </li>
            </ul>

            <p>
              Det ærlige poenget er dette: digital reservasjon løser ikke alle
              problemer. Men det kan hjelpe restauranter med å samle
              reservasjonsdetaljer bedre og gjøre daglige servicebeslutninger
              enklere.
            </p>
          </div>
        </section>

        <section id="for-whom" className="acquaintance">
          <h2>Hvilke restauranter har mest nytte av online reservasjon?</h2>

          <p>
            Online reservasjon er ikke like viktig for alle konsepter. Den er
            spesielt nyttig der bordplanlegging, travle tider eller
            gruppereservasjoner betyr mye.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Spesielt nyttig for</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Restauranter med kveldsrush.</li>
                <li className="text__seo-li">Familierestauranter.</li>
                <li className="text__seo-li">Pizzeriaer med sitteplasser.</li>
                <li className="text__seo-li">Brunsj-kafeer.</li>
                <li className="text__seo-li">Turistrestauranter.</li>
                <li className="text__seo-li">Hotellrestauranter.</li>
                <li className="text__seo-li">Restauranter med terrasse.</li>
                <li className="text__seo-li">Gruppereservasjoner.</li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Mindre kritisk for</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Rene takeaway-konsepter.</li>
                <li className="text__seo-li">Food trucks.</li>
                <li className="text__seo-li">
                  Svært små kafeer med bare noen få bord.
                </li>
                <li className="text__seo-li">Virksomheter uten sitteplasser.</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Vanlige feil med online reservasjon</h2>

            <p>
              Online reservasjon hjelper bare når flyten er enkel, tydelig og
              koblet til den virkelige restaurantdriften.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige feil</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Skjemaet ber om for mye informasjon.
                  </li>
                  <li className="text__seo-li">
                    Gjester vet ikke om reservasjonen er bekreftet.
                  </li>
                  <li className="text__seo-li">
                    Mobilopplevelsen er svak.
                  </li>
                  <li className="text__seo-li">
                    Reservasjonsknappen er skjult.
                  </li>
                  <li className="text__seo-li">
                    Teamet ser reservasjoner for sent.
                  </li>
                  <li className="text__seo-li">
                    Det finnes ikke et telefonalternativ for gjester som
                    foretrekker å ringe.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Hold skjemaet kort og tydelig.
                  </li>
                  <li className="text__seo-li">
                    Forklar om det er en forespørsel eller en bekreftet booking.
                  </li>
                  <li className="text__seo-li">
                    Sørg for at skjemaet fungerer godt på smarttelefon.
                  </li>
                  <li className="text__seo-li">
                    Plasser reservasjonsknappen etter menyen.
                  </li>
                  <li className="text__seo-li">
                    Varsle restauranten tydelig.
                  </li>
                  <li className="text__seo-li">
                    La telefon og online reservasjon fungere sammen.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <SeoWorkSteps lang="no" />

        <section id="faq" className="acquaintance">
          <h2>Vanlige spørsmål om online bordreservasjon</h2>

          <div className="seoFaq">
            <details>
              <summary>Trenger alle restauranter online reservasjon?</summary>
              <p>
                Nei. For rene takeaway-konsepter eller svært små virksomheter
                kan det være mindre viktig. For restauranter med sitteplasser,
                travle tider eller gruppereservasjoner gir det mer mening å
                teste dette.
              </p>
            </details>

            <details>
              <summary>Erstatter online reservasjon telefonen?</summary>
              <p>
                Ikke nødvendigvis. Den utfyller ofte telefonen og gir gjester en
                ekstra, mer praktisk kanal.
              </p>
            </details>

            <details>
              <summary>Kan gjester reservere utenfor åpningstid?</summary>
              <p>
                Ja. Det er en av hovedfordelene: gjester kan sende en forespørsel
                selv når ingen er tilgjengelig på telefon.
              </p>
            </details>

            <details>
              <summary>Kan online reservasjon forhindre no-shows?</summary>
              <p>
                Den kan ikke forhindre dem helt. Men den kan hjelpe med å samle
                kontaktinformasjon og reservasjonsdetaljer tydeligere.
              </p>
            </details>

            <details>
              <summary>Kan reservasjon kobles sammen med en digital meny?</summary>
              <p>
                Ja. Dette er en veldig naturlig vei: gjesten sjekker menyen og
                kan deretter reservere bord med en gang.
              </p>
            </details>

            <details>
              <summary>Trenger gjesten en app?</summary>
              <p>
                Nei. Den beste opplevelsen er vanligvis en nettleserbasert
                reservasjonsflyt på smarttelefon.
              </p>
            </details>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Flere temaer om digitale menyer og reservasjon</h2>

          <div className="seoCompareSmall">
            <p>
              <Link to="/no/digital-drikkemeny-bar">
                Digital drikkemeny for barer
              </Link>
            </p>

            <p>
              <Link to="/no/restaurant-nettside">
                Restaurantnettsted, reservasjoner og tillit på ett sted.
              </Link>
            </p>

            <p>
              <Link to="/no/digitalt-bestillingssystem-for-restauranter">
                Digitalt bestillingssystem for restauranter
              </Link>
            </p>

            <p>
              <Link to="/no/restaurant-digitalisering">
                Den gradvise overgangen av restauranter til digitale teknologier
              </Link>
            </p>

            <p>
              <Link to="/no/digital-meny-sammenligning">
                Sammenligning av leverandører av digitale menyer
              </Link>
            </p>
          </div>
        </section>
      </article>
    </main>
  );
}